// Code generated by cmd/herrgen. DO NOT EDIT.
// Source: the `herr.Code` declarations in the Go services.
// Regenerate with `make herrgen` (or `go run ./cmd/herrgen`).

/**
 * Error codes declared by the Go services, generated so the TypeScript control
 * plane fails to compile when a Go service gains a code that has no
 * customer-facing presentation.
 *
 * The doc text on each entry is the ENGINEER-facing Go doc comment. It is NOT
 * customer copy and must never be rendered to a user — it exists to inform
 * whoever writes the presentation entry.
 */
export const goErrorCodes = {
  /**
   * ErrAgentError — marks a turn the agent itself reported as failed (an error
   * terminal on the worker's event stream — e.g. its LLM call was rejected).
   * The worker is alive and answered deterministically, so the control plane
   * must fail the turn immediately with this code instead of the generic
   * worker-stopped path. When the LLM proxy captured the gateway's herr for
   * the failed call, it rides as a REASON on this herr — the full typed cause
   * chain crosses every wire (herr ⇄ the control plane's DomainError are the
   * same model).
   *
   * @source services/langyagent/domain/errors.go
   */
  agent_error: { service: "langyagent" },
  /**
   * ErrSessionNotFound — signals the worker's internal agent session vanished
   * mid-turn. The orchestrator recycles the worker and surfaces a typed
   * "session-not-found" event.
   *
   * @source services/langyagent/domain/errors.go
   */
  agent_session_not_found: { service: "langyagent", httpStatus: 404 },
  /**
   * ErrAuthUpstream
   *
   * @source services/aigateway/domain/errors.go
   */
  auth_upstream_unavailable: { service: "aigateway", httpStatus: 503 },
  /**
   * ErrBadRequest — is the catch-all for shape errors that don't have a more
   * specific code.
   *
   * Also declared by aigateway (ErrBadRequest), rpc (CodeBadRequest).
   *
   * @source pkg/rpc/decode.go
   * @source services/aigateway/domain/errors.go
   * @source services/nlpgo/domain/errors.go
   */
  bad_request: { service: "nlpgo", httpStatus: 400 },
  /**
   * ErrBudgetExceeded
   *
   * @source services/aigateway/domain/errors.go
   */
  budget_exceeded: { service: "aigateway", httpStatus: 402 },
  /**
   * ErrChainExhausted
   *
   * @source services/aigateway/domain/errors.go
   */
  chain_exhausted: { service: "aigateway", httpStatus: 502 },
  /**
   * ErrChildUnavailable — signals the uvicorn child process is down or
   * unreachable.
   *
   * @source services/nlpgo/domain/errors.go
   */
  child_unavailable: { service: "nlpgo", httpStatus: 503 },
  /**
   * ErrCircuitOpen
   *
   * @source services/aigateway/domain/errors.go
   */
  circuit_open: { service: "aigateway", httpStatus: 503 },
  /**
   * ErrCodeBlockTimeout — signals the user code subprocess exceeded
   * NLPGO_ENGINE_CODE_BLOCK_TIMEOUT_SECONDS and was killed.
   *
   * Also produced as a workflow NodeError type, so this one entry is the copy
   * for both the HTTP failure and the node error event. Its node sites are
   * among the @source files below.
   *
   * @source services/nlpgo/app/engine/engine.go
   * @source services/nlpgo/domain/errors.go
   */
  code_block_timeout: { service: "nlpgo", httpStatus: 504 },
  /**
   * ErrCodexSessionExpired — means the codex provider's OAuth session is dead
   * (refresh rejected) — the user must sign in with OpenAI again. Clients
   * receive it as a 401 with this code so Langy can render the re-authenticate
   * card instead of a generic provider error.
   *
   * @source services/aigateway/domain/errors.go
   */
  codex_session_expired: { service: "aigateway", httpStatus: 401 },
  /**
   * ConfigInvalid — is the herr code for configuration validation failures.
   *
   * @source pkg/config/validate.go
   */
  config_invalid: { service: "config" },
  /**
   * ErrConversationBusy — signals a second concurrent turn for a conversation
   * whose single-stream agent session is already answering. The control plane
   * shows a "still answering — wait" notice. Maps to 409.
   *
   * @source services/langyagent/domain/errors.go
   */
  conversation_busy: { service: "langyagent", httpStatus: 409 },
  /**
   * ErrCredentialsRequired — signals the control plane asked us to serve a
   * turn with NO LangWatch session key, but we have no live worker to serve it
   * with — so we must spawn, and a spawn needs a key.
   *
   * This is the designed resolution of a race, not a failure: the control
   * plane probes for a live worker and omits the key when it finds one (a
   * reused worker keeps the key in its env, so minting another would be
   * waste). The worker can die in the gap between that probe and the turn.
   * Rather than have the control plane defensively mint a key on every turn —
   * which is exactly the credential sprawl we removed — it mints once, on
   * demand, when we say this. Maps to 428 Precondition Required: the caller
   * must supply something before we can proceed, and retrying with it will
   * work.
   *
   * @source services/langyagent/domain/errors.go
   */
  credentials_required: { service: "langyagent", httpStatus: 428 },
  /**
   * ErrEndUserRequired — A per-end-user budget template is active on this key
   * and the request carried no end-user id: fail closed, a cap evadable by
   * omitting a field is not a cap.
   *
   * @source services/aigateway/domain/errors.go
   */
  end_user_required: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrGatewayUnavailable — signals the AI Gateway returned an error or is
   * unreachable.
   *
   * @source services/nlpgo/domain/errors.go
   */
  gateway_unavailable: { service: "nlpgo", httpStatus: 502 },
  /**
   * ErrGuardrailBlocked
   *
   * @source services/aigateway/domain/errors.go
   */
  guardrail_blocked: { service: "aigateway", httpStatus: 403 },
  /**
   * ErrGuardrailUpstreamUnavailable — means the guardrail could not be
   * evaluated at all. Fail-closed keys stop here rather than proceeding as
   * though the guardrail had passed. Contract 5.
   *
   * @source services/aigateway/domain/errors.go
   */
  guardrail_upstream_unavailable: { service: "aigateway", httpStatus: 503 },
  /**
   * ErrIdleTimeout — signals the SSE stream went silent past
   * NLPGO_ENGINE_STREAM_IDLE_TIMEOUT_SECONDS and the engine closed the
   * connection.
   *
   * @source services/nlpgo/domain/errors.go
   */
  idle_timeout: { service: "nlpgo", httpStatus: 504 },
  /**
   * ErrInternal — is the generic fallback for unexpected errors.
   *
   * Also declared by aigateway (ErrInternal), nlpgo (ErrInternal).
   *
   * @source pkg/httpmiddleware/recover.go
   * @source services/aigateway/domain/errors.go
   * @source services/langyagent/domain/errors.go
   * @source services/nlpgo/domain/errors.go
   */
  internal_error: { service: "langyagent", httpStatus: 500 },
  /**
   * ErrInvalidAPIKey
   *
   * @source services/aigateway/domain/errors.go
   */
  invalid_api_key: { service: "aigateway", httpStatus: 401 },
  /**
   * ErrInvalidConversationID — signals a conversationId that is not a safe
   * path segment (would escape SESSIONS_ROOT).
   *
   * @source services/langyagent/domain/errors.go
   */
  invalid_conversation_id: { service: "langyagent", httpStatus: 422 },
  /**
   * ErrInvalidDataset — signals dataset-specific validation failures (column
   * length mismatch, entry_selection out of range, split sizes exceeding row
   * count).
   *
   * Also produced as a workflow NodeError type, so this one entry is the copy
   * for both the HTTP failure and the node error event. Its node sites are
   * among the @source files below.
   *
   * @source services/nlpgo/app/engine/engine.go
   * @source services/nlpgo/app/engine/evaluation.go
   * @source services/nlpgo/domain/errors.go
   */
  invalid_dataset: { service: "nlpgo", httpStatus: 400 },
  /**
   * ErrInvalidWorkflow — signals a malformed workflow JSON (cycles, unknown
   * node ids on edges, column-length mismatches in a dataset).
   *
   * @source services/nlpgo/domain/errors.go
   */
  invalid_workflow: { service: "nlpgo", httpStatus: 400 },
  /**
   * ErrJSONPathNoMatch — signals an HTTP block's output_path matched no
   * element of the upstream response.
   *
   * @source services/nlpgo/domain/errors.go
   */
  jsonpath_no_match: { service: "nlpgo", httpStatus: 422 },
  /**
   * llmUpstreamErrorCode — marks a failed mediated LLM call whose response
   * body was NOT the gateway's typed herr envelope — a provider-native error
   * the gateway forwarded verbatim (an Anthropic "credit balance too low", the
   * codex backend's `{"detail": ...}`). The provider's own error type rides as
   * a typed reason so the control plane can name the failure; the provider's
   * prose does not travel with it (see decodeLLMErrorBody).
   *
   * @source services/langyagent/adapters/otelrelay/llmproxy.go
   */
  llm_upstream_error: { service: "langyagent" },
  /**
   * ErrMaxWorkers — signals LANGY_MAX_WORKERS is reached. The chat
   * orchestrator converts it into a 200 ndjson
   * {type:"error",error:"at-capacity"} event so the control plane can show a
   * graceful "agent busy" instead of a 5xx — it never reaches WriteHTTP, but
   * is still registered (503) for completeness.
   *
   * @source services/langyagent/domain/errors.go
   */
  max_workers_reached: { service: "langyagent", httpStatus: 503 },
  /**
   * ErrMissingModel — is a request-shape error with its own stable identity so
   * clients and rejection metrics do not have to infer it from prose.
   *
   * @source services/aigateway/domain/errors.go
   */
  missing_model: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrModelNotAllowed
   *
   * @source services/aigateway/domain/errors.go
   */
  model_not_allowed: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrModelNotRecognized — means the request named a model that matches
   * nothing this key can place: no provider declares it, its name matches no
   * vendor the gateway can guess from, and the key holds more than one
   * provider that told us what it serves. Sending it down the chain anyway
   * makes every vendor answer for a model it never had, and the caller reads
   * the last vendor's error instead of the real problem. Distinct from
   * model_provider_not_bound, which is a provider the caller DID name.
   *
   * @source services/aigateway/domain/errors.go
   */
  model_not_recognized: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrProviderNotBound — means the request names a provider (explicit
   * "provider/model" prefix or alias) that has no credential slot on this VK.
   * Dispatching anyway would hand a mismatched credential to the provider
   * selected by the model prefix, which surfaces as opaque provider-config
   * errors ("deployments not set", HTML error pages).
   *
   * @source services/aigateway/domain/errors.go
   */
  model_provider_not_bound: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrNoFreeUID — signals every UID slot in the per-worker range is in use.
   * With 60_000 slots and a default MAX_WORKERS of 20 this cannot happen in
   * practice; surfaced rather than silently colliding when an operator raises
   * MAX_WORKERS above the slot capacity.
   *
   * @source services/langyagent/domain/errors.go
   */
  no_free_worker_uid: { service: "langyagent", httpStatus: 503 },
  /**
   * ErrNoProviderConfigured — means the virtual key's bundle carries zero
   * provider credentials — the organization has no ModelProvider configured.
   * Without this guard the dispatcher would hand Bifrost a zero-value
   * Credential and the caller would see an opaque "provider is required".
   *
   * @source services/aigateway/domain/errors.go
   */
  no_provider_configured: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrNotFound — is returned by handlers that match no resource.
   *
   * Also declared by aigateway (ErrNotFound).
   *
   * @source services/aigateway/domain/errors.go
   * @source services/nlpgo/domain/errors.go
   */
  not_found: { service: "nlpgo", httpStatus: 404 },
  /**
   * CodePayloadTooLarge
   *
   * Also declared by aigateway (ErrPayloadTooLarge).
   *
   * @source pkg/rpc/decode.go
   * @source services/aigateway/domain/errors.go
   */
  payload_too_large: { service: "rpc", httpStatus: 413 },
  /**
   * ErrPolicyViolation
   *
   * @source services/aigateway/domain/errors.go
   */
  policy_violation: { service: "aigateway", httpStatus: 403 },
  /**
   * ErrProviderConfigInvalid — means the provider slot is configured in a way
   * that cannot serve THIS request: no key declares the requested model, a
   * deployment map is missing, or the provider does not implement the
   * operation. Terminal, and the remediation is in the customer's model
   * provider settings rather than in the request.
   *
   * @source services/aigateway/domain/errors.go
   */
  provider_config_invalid: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrProviderConnectionFailed — means the request never got to the provider:
   * DNS failure, connection refused, transport error. Retryable and the
   * provider's (or the network's) fault, unlike the config and credential
   * codes above, which repeat identically on every attempt.
   *
   * Deliberately not "provider_unreachable": that slug is already an app code,
   * thrown when a credential CHECK finds nothing answering
   * (providerValidation.ts), and its customer copy says the key was never
   * checked. One slug cannot carry both meanings, and the copy for either
   * would be wrong on the other's path.
   *
   * @source services/aigateway/domain/errors.go
   */
  provider_connection_failed: { service: "aigateway", httpStatus: 502 },
  /**
   * ErrProviderCredentialInvalid — means the credentials configured for a
   * model provider cannot produce an authenticated call at all — a Vertex
   * service account that yields no OAuth token source, AWS credentials the
   * signer cannot retrieve. The request never reaches the provider, so there
   * is no upstream verdict to forward and no retry that can help: every
   * credential in the chain fails the same way. Terminal and the customer's to
   * fix, so it must not carry provider_timeout's retryable 504.
   *
   * @source services/aigateway/domain/errors.go
   */
  provider_credential_invalid: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrProviderCredentialRejected — means the credential reached the provider
   * and the provider refused it (401/403). Distinct from
   * provider_credential_invalid, which never got that far: this one proves the
   * credential is well-formed and says the account behind it is the problem —
   * expired, revoked, or lacking permission for the operation.
   *
   * Reserved: no path emits it today. A provider that refuses a credential
   * answers with a status, and errFromBifrost forwards every answered error
   * verbatim before classification runs, so the 401/403 arrives as itself.
   * Registered anyway (status, fault, remediation, presentation copy) so a
   * path that does not forward — a pre-dispatch credential refresh, a plugin
   * rejection — has a code to use without minting a new slug.
   *
   * @source services/aigateway/domain/errors.go
   */
  provider_credential_rejected: { service: "aigateway", httpStatus: 401 },
  /**
   * ErrProviderError
   *
   * @source services/aigateway/domain/errors.go
   */
  provider_error: { service: "aigateway", httpStatus: 502 },
  /**
   * ErrProviderTimeout
   *
   * @source services/aigateway/domain/errors.go
   */
  provider_timeout: { service: "aigateway", httpStatus: 504 },
  /**
   * ErrRateLimited
   *
   * @source services/aigateway/domain/errors.go
   */
  rate_limited: { service: "aigateway", httpStatus: 429 },
  /**
   * ErrRealtimeRegistryUnavailable — means the control plane could not record
   * the session, so the gateway refused to mint one. This is a deliberate
   * departure from the budget fail-open rule: an unrecorded session is voice
   * nobody can bill and a cap nobody can enforce.
   *
   * @source services/aigateway/domain/errors.go
   */
  realtime_registry_unavailable: { service: "aigateway", httpStatus: 503 },
  /**
   * ErrRealtimeSessionLimit — means the virtual key already holds as many open
   * realtime voice sessions as its realtime.maxOpenSessions allows. A voice
   * session bills for as long as it runs, so the arrival-rate limits do not
   * bound it and this is the only cap that does.
   *
   * @source services/aigateway/domain/errors.go
   */
  realtime_session_limit: { service: "aigateway", httpStatus: 429 },
  /**
   * ErrRequestAbandoned — means the caller disconnected or its deadline
   * expired before the provider answered. It is a verdict about the caller,
   * not about the credential: it must neither advance the fallback chain nor
   * move the circuit breaker, or one client hanging up repeatedly would open
   * the breaker on a healthy provider.
   *
   * @source services/aigateway/domain/errors.go
   */
  request_abandoned: { service: "aigateway", httpStatus: 499 },
  /**
   * ErrSSRFBlocked — signals an HTTP block tried to reach a destination
   * disallowed by the SSRF policy (loopback, private, link-local, metadata).
   *
   * Also produced as a workflow NodeError type, so this one entry is the copy
   * for both the HTTP failure and the node error event. Its node sites are
   * among the @source files below.
   *
   * @source services/nlpgo/app/engine/engine.go
   * @source services/nlpgo/domain/errors.go
   */
  ssrf_blocked: { service: "nlpgo", httpStatus: 400 },
  /**
   * ErrUnauthorized — signals a missing or invalid internal bearer secret on
   * /chat. The manager binds the cluster service surface, so this is the wall
   * against any pod that can route to it.
   *
   * Also declared by nlpgo (ErrUnauthorized).
   *
   * @source services/langyagent/domain/errors.go
   * @source services/nlpgo/domain/errors.go
   */
  unauthorized: { service: "langyagent", httpStatus: 401 },
  /**
   * CodeUnprocessable — is a syntactically valid request whose fields fail
   * validation — 422, distinct from CodeBadRequest (unreadable/unparseable).
   *
   * @source pkg/rpc/decode.go
   */
  unprocessable_entity: { service: "rpc", httpStatus: 422 },
  /**
   * ErrUnsupportedNodeKind — signals the workflow contains a node kind not yet
   * supported by the Go engine (agent, evaluator, retriever, custom). Triggers
   * a 501 so the TS app can fall back to Python.
   *
   * Also produced as a workflow NodeError type, so this one entry is the copy
   * for both the HTTP failure and the node error event. Its node sites are
   * among the @source files below.
   *
   * @source services/nlpgo/app/engine/engine.go
   * @source services/nlpgo/domain/errors.go
   */
  unsupported_node_kind: { service: "nlpgo", httpStatus: 501 },
  /**
   * ErrUnsupportedParameter — means the parameter policy refused a request
   * parameter for the target lane: either the request depends on it
   * functionally and the lane cannot honor it, or drop_tuning_params is false
   * and the lane has no mapping for it. The code matches OpenAI's own
   * parameter rejections so SDK error handling stays familiar.
   *
   * @source services/aigateway/domain/errors.go
   */
  unsupported_parameter: { service: "aigateway", httpStatus: 400 },
  /**
   * ErrUpstreamHTTP — signals a non-2xx from an HTTP block's upstream.
   *
   * Also produced as a workflow NodeError type, so this one entry is the copy
   * for both the HTTP failure and the node error event. Its node sites are
   * among the @source files below.
   *
   * @source services/nlpgo/app/engine/engine.go
   * @source services/nlpgo/domain/errors.go
   */
  upstream_http_error: { service: "nlpgo", httpStatus: 502 },
  /**
   * ErrKeyDisabled — is the REVERSIBLE stop: the key material is intact and an
   * administrator can re-enable it. Distinct from revoked (one-way) so tenant
   * tooling can branch on which one it is.
   *
   * @source services/aigateway/domain/errors.go
   */
  virtual_key_disabled: { service: "aigateway", httpStatus: 403 },
  /**
   * ErrKeyExpired — is the stop nobody pressed: the key carries an expiration
   * date and that date has passed. The key material is intact and the key is
   * still ACTIVE in the control plane, so the fix is a new date rather than a
   * new secret. Distinct from revoked and disabled so a tenant can tell
   * "extend it" from "ask an administrator" from "mint a new one".
   *
   * @source services/aigateway/domain/errors.go
   */
  virtual_key_expired: { service: "aigateway", httpStatus: 403 },
  /**
   * ErrKeyRevoked
   *
   * @source services/aigateway/domain/errors.go
   */
  virtual_key_revoked: { service: "aigateway", httpStatus: 403 },
  /**
   * ErrWorkerNotReady — signals a freshly spawned worker did not become ready
   * within LANGY_READINESS_TIMEOUT_MS.
   *
   * @source services/langyagent/domain/errors.go
   */
  worker_not_ready: { service: "langyagent", httpStatus: 500 },
  /**
   * ErrWorkerSpawn — signals a worker subprocess could not be created (home
   * setup, port allocation, process start).
   *
   * @source services/langyagent/domain/errors.go
   */
  worker_spawn_failed: { service: "langyagent", httpStatus: 500 },
} as const;

export type GoErrorCode = keyof typeof goErrorCodes;

/**
 * Workflow NodeError codes produced by the nlpgo engine (the `Type` field of
 * `NodeError`). Generated for the same reason as `goErrorCodes`: the client
 * presentation registry is exhaustive over them, so a new node-error type in
 * Go fails the TypeScript build until its customer copy is written.
 */
export const nodeErrorCodes = {
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  agent_missing_type: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  agent_missing_workflow_id: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  agent_unauthorized: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  agent_unconfigured: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  agent_unknown_type: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  agent_workflow_error: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  agent_workflow_executor_unavailable: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/attachment.go
   */
  attachment_fetch_error: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  code_runner_error: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  code_runner_unavailable: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   * @source services/nlpgo/app/engine/evaluation.go
   * @source services/nlpgo/app/engine/stream.go
   */
  context_canceled: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  custom_missing_workflow_id: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  custom_unauthorized: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  custom_unconfigured: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  custom_workflow_error: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  custom_workflow_executor_unavailable: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/evaluation.go
   */
  engine_error: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  evaluator_error: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  evaluator_executor_unavailable: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  evaluator_missing_slug: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  evaluator_unauthorized: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  evaluator_unconfigured: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  http_error: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  http_executor_unavailable: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  invalid_condition: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  llm_error: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  llm_executor_unavailable: { service: "nlpgo" },
  /**
   * @source services/nlpgo/app/engine/engine.go
   */
  llm_model_not_set: { service: "nlpgo" },
} as const;

export type NodeErrorCode = keyof typeof nodeErrorCodes;
